import { useEffect, useState, type ChangeEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Input } from "@agentnative/ui";
import { apiErrorMessage } from "../lib/api/client";
import { getOrganization, listOrganizations } from "../lib/api/org";

export interface PickedOrg {
  id: string;
  /** Present once known (picked from search, or resolved from an id). */
  name?: string;
  slug?: string;
}

interface Props {
  value: PickedOrg | null;
  onChange: (org: PickedOrg | null) => void;
  label?: string;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/**
 * Staff organization picker: typeahead over the staff-only ListOrganizations
 * RPC (name or slug substring), storing the organization id. If the org is not
 * found, staff can switch to pasting an id, which is resolved to a name with
 * GetOrganization so the confirmation line can still show it.
 */
export function OrganizationPicker({ value, onChange, label = "Organization" }: Props) {
  const [text, setText] = useState("");
  const [pasteMode, setPasteMode] = useState(false);
  const [pasted, setPasted] = useState("");
  const debounced = useDebounced(text.trim(), 250);

  const search = useQuery({
    queryKey: ["admin-org-picker", debounced],
    queryFn: () => listOrganizations({ query: debounced, limit: 10 }),
    enabled: !value && !pasteMode,
  });

  // Resolve a name for an id we only have as text (deep link or pasted id).
  const needsName = !!value && !value.name && UUID_RE.test(value.id);
  const resolved = useQuery({
    queryKey: ["admin-org-resolve", value?.id],
    queryFn: () => getOrganization(value!.id),
    enabled: needsName,
    retry: false,
  });
  const resolvedOrg = resolved.data?.organization;
  useEffect(() => {
    if (value && !value.name && resolvedOrg && resolvedOrg.id === value.id) {
      onChange({ id: value.id, name: resolvedOrg.name, slug: resolvedOrg.slug });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedOrg]);

  if (value) {
    return (
      <div className="space-y-1">
        <div className="text-sm font-medium">{label}</div>
        <div className="flex items-center justify-between gap-3 rounded border p-3">
          <div className="min-w-0">
            {value.name ? (
              <>
                <div className="font-medium">{value.name}</div>
                <div className="text-sm opacity-70">
                  {value.slug} <span className="font-mono">({value.id})</span>
                </div>
              </>
            ) : (
              <>
                <div className="font-mono text-sm break-all">{value.id}</div>
                {resolved.isFetching && <div className="text-sm opacity-70">Looking up name...</div>}
                {resolved.isError && (
                  <div className="text-sm opacity-70">Name not found; this id will be used as entered.</div>
                )}
              </>
            )}
          </div>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              onChange(null);
              setText("");
              setPasted("");
            }}
          >
            Change
          </Button>
        </div>
      </div>
    );
  }

  if (pasteMode) {
    return (
      <div className="space-y-2">
        <Input
          label={`${label} ID`}
          required
          value={pasted}
          placeholder="org-uuid"
          onChange={(e: ChangeEvent<HTMLInputElement>) => setPasted(e.target.value)}
          helperText="Paste the id of an organization that search did not find."
        />
        <div className="flex gap-2">
          <Button type="button" disabled={!pasted.trim()} onClick={() => onChange({ id: pasted.trim() })}>
            Use this ID
          </Button>
          <Button type="button" variant="secondary" onClick={() => setPasteMode(false)}>
            Back to search
          </Button>
        </div>
      </div>
    );
  }

  const results = search.data?.organizations ?? [];
  return (
    <div className="space-y-2">
      <Input
        label={label}
        required
        value={text}
        placeholder="Search by name or slug"
        onChange={(e: ChangeEvent<HTMLInputElement>) => setText(e.target.value)}
      />
      {search.isLoading && <div className="text-sm opacity-70">Searching...</div>}
      {search.isError && (
        <Alert variant="danger" title="Organization search failed">
          {apiErrorMessage(search.error)} You can paste an organization ID instead.
        </Alert>
      )}
      {search.isSuccess && results.length === 0 && (
        <div className="text-sm opacity-70">
          {debounced ? `No organization matches "${debounced}".` : "No organizations."}
        </div>
      )}
      {results.length > 0 && (
        <ul className="divide-y rounded border" role="listbox" aria-label="Matching organizations">
          {results.map((o) => (
            <li key={o.id}>
              <button
                type="button"
                role="option"
                aria-selected={false}
                className="w-full px-3 py-2 text-left hover:bg-black/5"
                onClick={() => onChange({ id: o.id, name: o.name, slug: o.slug })}
              >
                <span className="font-medium">{o.name}</span>{" "}
                <span className="text-sm opacity-70">{o.slug}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {search.isSuccess && Number(search.data.total) > results.length && (
        <div className="text-sm opacity-70">
          Showing {results.length} of {String(search.data.total)}; type more to narrow.
        </div>
      )}
      <button type="button" className="text-sm underline" onClick={() => setPasteMode(true)}>
        Not listed? Paste an organization ID
      </button>
    </div>
  );
}
